import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {AgGridReact} from "ag-grid-react";
import {LocalizationAgGrid} from "../../utils/localization-ag-grid";
import {FaEraser, FaPlusSquare} from 'react-icons/fa';
import {useQuery} from '@tanstack/react-query';
import {useNavigate} from "react-router-dom";
import TemplateDefault from "../../components/template-default";
import Card from "../../components/card";
import Button from '../../components/button';
import InputForm from '../../components/input-form';
import SelectComponent from '../../components/select-component';
import TrainingsService from '../../services/training-service';
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css"
import InputDateTime from '../../components/input-datetime';
import general from "../../utils/general";
import {buildAccentInsensitiveColDef, setAccentInsensitiveQuickFilter} from "../../utils/ag-grid-quick-filter";

let initialFilters = {
    user_project: "",
    user_name: "",
    date_init: "",
    date_end: "",
}

const Training = () => {
    const navigate = useNavigate();
    const [filters, setFilters] = useState(initialFilters);
    const [selectedInitDate, setSelectedInitDate] = useState(null);
    const [selectedEndDate, setSelectedEndDate] = useState(null);
    const [trainings, setTrainings] = useState();
    const gridRef = useRef();
    const handleSelect = (id, value) => setFilters(prev => ({...prev, [id]: value}));

    const clearFilters = () => {
        setFilters(initialFilters);
        setSelectedInitDate(null);
        setSelectedEndDate(null);
    }

    useEffect(() => {
        if (selectedEndDate && selectedInitDate) {
            setFilters(prev => ({...prev, date_init: selectedInitDate, date_end: selectedEndDate}));
        } else {
            setFilters(prev => ({...prev, date_init: "", date_end: ""}));
        }
    }, [selectedInitDate, selectedEndDate]);

    useEffect(() => {
        document.title = "SISTEMA INTERNO";
    }, []);

    useQuery({
        queryFn: () => TrainingsService.list(filters).then((data) => {
            Object.keys(data).forEach(key => data[key] = data[key] ?? "");

            data.data.forEach(key => {
                key.date_init = key.date_init + " a " + key.date_end
            })

            setTrainings(data.data);

            return data;
        }),
        queryKey: ['trainings', {filters}]
    });

    const {data: users} = useQuery({
        queryFn: () => TrainingsService.users().then(({data}) => data),
        queryKey: ['users']
    });

    const {data: projects} = useQuery({
        queryFn: () => TrainingsService.projects().then(({data}) => data),
        queryKey: ['projects']
    });

    const defaultColDef = useMemo(() => {
        return buildAccentInsensitiveColDef({resizable: false});
    }, []);

    const onFilterTextBoxChanged = useCallback(({target}) => {
        setAccentInsensitiveQuickFilter(gridRef, target.value);
    }, []);

    let usersClients = [];
    if (users) {
        users.forEach(element => {
            usersClients.push({
                value: element.user_name,
                label: element.user_name
            });
        });
    }

    let projectsData = [];
    if (projects) {
        projects.forEach(element => {
            projectsData.push({
                value: element.name,
                label: element.name
            });
        });
    }

    const [columnDefs] = useState([
        {field: "id", headerName: "Id", maxWidth: 45},
        {field: "date_init", headerName: "Período", maxWidth: 200},
        {field: "user_project", headerName: "Projeto (cidade)", flex: 1, minWidth: 250},
        {field: "module", headerName: "Módulos", flex: 1, minWidth: 250},
        {field: "type", headerName: "Tipo", minWidth: 250},
        {
            field: "duration",
            headerName: "Duração",
            comparator: (a, b, nodeA, nodeB, isInverted) => {
                return general.hourComparator(a, b, nodeA, nodeB, isInverted);
            }
        },
    ]);

    const onCellClicked = (cell) => {
        navigate(`/after-sales/trainings/form/${cell.data.id}`)
    };

    return (
        <TemplateDefault title="Treinamentos">
            <Card>
                <div className="flex flex-col lg:grid grid-cols-1 gap-x-6 gap-y-4 lg:grid-cols-12">
                    <Button col="col-span-2" onClick={() => navigate("/after-sales/trainings/form")}>
                        <FaPlusSquare /> Novo treinamento
                    </Button>

                    <div className={"col-span-2"}>
                        <InputDateTime
                            id={"date-init"}
                            label={"Data abertura (inicio)"}
                            selected={selectedInitDate}
                            onChange={date => setSelectedInitDate(date)}
                        />
                    </div>

                    <div className={"col-span-2"}>
                        <InputDateTime
                            id={"date-end"}
                            label={"Data abertura (fim)"}
                            selected={selectedEndDate}
                            onChange={date => setSelectedEndDate(date)}
                        />
                    </div>

                    <SelectComponent
                        col="col-span-3"
                        instanceId="user_project"
                        label="Cidades"
                        options={projectsData}
                        value={filters.user_project}
                        onChange={(value) => handleSelect("user_project", value)}
                        name="user_project"
                    />

                    <SelectComponent
                        col="col-span-3"
                        instanceId="user_name"
                        label="Usuário (Cliente)"
                        options={usersClients}
                        onChange={(value) => handleSelect("user_name", value)}
                        value={filters.user_name}
                        name="user_name"
                    />

                    <div className="col-span-12">
                        <div className="flex justify-between">
                            <InputForm
                                id="search"
                                label="Pesquisar"
                                col="w-[calc(100%-60px)]"
                                type="search"
                                onChange={onFilterTextBoxChanged}
                            />
                            <div>
                                <Button onClick={clearFilters}>
                                    <FaEraser />
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>

                <div className={"ag-theme-quartz grid mt-4"} style={{height: "calc(100vh - 207px)"}}>
                    <AgGridReact
                        ref={gridRef}
                        rowHeight={32}
                        rowData={trainings}
                        columnDefs={columnDefs}
                        defaultColDef={defaultColDef}
                        pagination
                        paginationPageSize={500}
                        paginationPageSizeSelector={[100, 200, 300, 400, 500]}
                        animateRows={false}
                        suppressRowClickSelection={true}
                        suppressCellFocus={true}
                        onCellClicked={onCellClicked}
                        gridOptions={{localeText: LocalizationAgGrid}}
                    />
                </div>
            </Card>
        </TemplateDefault>
    );
};

export default Training;
